import {Popper, PopperProps, styled} from '@mui/material';
import MuiAutocomplete, {
  autocompleteClasses,
  AutocompleteRenderInputParams,
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import {ReactElement, ReactNode, useCallback} from 'react';
import {SetRequired} from 'type-fest';

import ClearIcon from 'components/icons/cross.svg';
import DropdownIcon from 'components/icons/dropdown.svg';
import LoadingIndicator from 'components/LoadingIndicator';

import TextField, {TextFieldProps} from './TextField';

export type AutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> & {
  label?: ReactNode;
  secondLabel?: string;
  TextFieldProps?: Omit<TextFieldProps, 'ref'>;
  renderInput?: (params: AutocompleteRenderInputParams, label?: string) => ReactNode;
  popperMaxHeight?: number;
} & DataTestId;

export const MOBILE_LISTBOX_3_ELEMENTS_HEIGHT = 150;

const SPopper = styled(Popper, {shouldForwardProp: propName => propName !== 'maxHeight'})<{maxHeight?: number}>(
  ({maxHeight}) => ({
    [`& .${autocompleteClasses.listbox}`]: {
      maxHeight,
    },
  })
);

const PopperSlot = ({maxHeight, ...props}: PopperProps & {maxHeight?: number}) => {
  return <SPopper {...props} maxHeight={maxHeight} />;
};

const SLoadingIndicator = styled(LoadingIndicator)(() => ({
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
}));

export default function Autocomplete<
  T extends {id: string; label?: ReactElement | string | number},
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  label,
  getOptionLabel,
  TextFieldProps = {},
  classes = {},
  dataTestId,
  popperMaxHeight,
  loading,
  ...props
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const renderInput = useCallback(
    ({InputLabelProps: _, ...params}: AutocompleteRenderInputParams) => {
      return (
        <TextField
          {...params}
          label={label}
          // fix https://github.com/mui/material-ui/issues/41406
          onKeyDown={e => e.stopPropagation()}
          {...TextFieldProps}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              ...TextFieldProps.slotProps?.htmlInput,
            },
            input: {
              ...params.InputProps,
              ...TextFieldProps.slotProps?.input,
              endAdornment: loading ? <SLoadingIndicator size={16} padding={0} /> : params.InputProps.endAdornment,
            },
          }}
        />
      );
    },
    [label, loading, TextFieldProps]
  );

  const renderTags = useCallback<
    SetRequired<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderTags'>['renderTags']
  >(
    (values, getTagProps) =>
      values.map((value, index) => {
        const {key, ...props} = getTagProps({index});
        const label = getOptionLabel?.(value) || value.label || '';
        return (
          <Tooltip key={key} title={label}>
            <Chip label={label} size='small' {...props} />
          </Tooltip>
        );
      }),
    [getOptionLabel]
  );

  return (
    <MuiAutocomplete
      classes={classes}
      getOptionLabel={getOptionLabel}
      renderInput={renderInput}
      renderTags={renderTags}
      popupIcon={<DropdownIcon />}
      clearIcon={<ClearIcon width={16} height={16} />}
      data-testid={dataTestId}
      slots={{
        ...props.slots,
        ...(popperMaxHeight ? {popper: PopperSlot} : {}),
      }}
      slotProps={{
        ...props.slotProps,
        popper: {
          ...(props.slotProps?.popper || {}),
          maxHeight: popperMaxHeight,
        } as unknown as PopperProps,
      }}
      {...props}
    />
  );
}
